import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, AlertTriangle, Clock, Send, RefreshCw, Users, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Dashboard Widget: Founders Expiring Soon
 * Shows founders whose trials are expiring within 7 days
 * Allows quick-send of reminder emails
 */
const FoundersExpiringSoonWidget = ({ className }) => {
  const navigate = useNavigate();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    fetchExpiringSoon();
  }, []);
  
  const fetchExpiringSoon = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/founder-notifications/expiring-soon`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFounders(data.founders || []);
      }
    } catch (error) {
      console.error('Error fetching expiring founders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const sendReminders = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/founder-notifications/process-reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Sent ${data.results?.emails_sent || 0} reminder emails!`);
        fetchExpiringSoon(); // Refresh
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reminders');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setProcessing(false);
    }
  };
  
  const getUrgencyStyle = (days) => {
    if (days <= 0) return { bg: 'bg-gray-500', text: 'Expired' };
    if (days <= 1) return { bg: 'bg-red-500', text: '1 day' };
    if (days <= 3) return { bg: 'bg-orange-500', text: `${days} days` };
    return { bg: 'bg-amber-500', text: `${days} days` };
  };
  
  // Don't show widget if no founders expiring
  if (!loading && founders.length === 0) {
    return null;
  }
  
  return (
    <Card className={`border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Crown className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-base text-amber-900">Founders Expiring Soon</CardTitle>
              <CardDescription className="text-amber-700">
                {loading ? 'Loading...' : `${founders.length} trial${founders.length !== 1 ? 's' : ''} expiring within 7 days`}
              </CardDescription>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchExpiringSoon}
            disabled={loading}
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-amber-500" />
          </div>
        ) : founders.length > 0 ? (
          <>
            {/* Founders List (max 5) */}
            <div className="space-y-2">
              {founders.slice(0, 5).map((founder, index) => {
                const urgency = getUrgencyStyle(founder.days_remaining);
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-white rounded-lg border border-amber-100"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-amber-700">
                          {founder.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{founder.name}</p>
                        <p className="text-xs text-gray-500 truncate">{founder.email}</p>
                      </div>
                    </div>
                    <Badge className={`${urgency.bg} text-white text-xs flex-shrink-0`}>
                      {founder.days_remaining <= 0 ? (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      ) : (
                        <Clock className="w-3 h-3 mr-1" />
                      )}
                      {urgency.text}
                    </Badge>
                  </div>
                );
              })}
              
              {founders.length > 5 && (
                <p className="text-xs text-amber-600 text-center">
                  +{founders.length - 5} more founders
                </p>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                onClick={sendReminders}
                disabled={processing}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {processing ? (
                  <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-3 h-3 mr-1" /> Send Reminders</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/admin/integrations')}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                Manage <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <Users className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-gray-600">All founders are doing great!</p>
            <p className="text-xs text-gray-400">No trials expiring within 7 days</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FoundersExpiringSoonWidget;
